import Fuse from "fuse.js";

import { foldText } from "@/utils/text";

import type { MedicationRef } from "../../../catalogs/supported-medications";

export type MedicationMatch = {
  group: string;
  name: string;
  score: number;
};

type CatalogEntry = {
  group: string;
  name: string;
  normalized: string;
};

export function findMedicationMatches(
  input: string,
  catalog: readonly MedicationRef[],
  opts?: { threshold?: number; maxResults?: number; minLength?: number },
): MedicationMatch[] {
  const { threshold = 0.45, maxResults = 3, minLength = 3 } = opts ?? {};

  const normalizedInput = normalize(input);
  if (normalizedInput.length < minLength) return [];

  const entries: CatalogEntry[] = catalog.map((m) => ({
    group: m.group,
    name: m.name,
    normalized: normalize(m.name),
  }));

  const fuse = new Fuse(entries, {
    keys: ["normalized"],
    threshold,
    minMatchCharLength: minLength,
    includeScore: true,
    ignoreLocation: true,
  });

  const fuseResults = fuse.search(normalizedInput);

  const results: MedicationMatch[] = fuseResults.map(({ item, score = 1 }) => ({
    group: item.group,
    name: item.name,
    score: 1 - score,
  }));

  results.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    return a.name.localeCompare(b.name);
  });

  return results.slice(0, maxResults);
}

/**
 * Folds diacritics, then reduces anything else (punctuation, dosage symbols) to
 * single spaces so Fuse scores on bare words. `foldText` maps stroke letters to
 * their base letter before the strip, so "Metylprednizolón" and a hypothetical
 * "Łacidofil" keep every letter rather than losing one to the `[^a-z0-9 ]` pass.
 */
function normalize(s: string): string {
  return foldText(s)
    .replace(/[^a-z0-9 ]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}
